<template>
  <div class="h-full">
    <Header
      v-if="!$apollo.loading && !errors.length"
      :sending-test="sendingTest"
      :mobile-send="mobileSend"
      :mobile-sidebar="mobileSidebar"
      :loading="$apollo.loading"
      :account-id="accountId"
      @test-send="onTestSend"
      @mobile-nav="onMobileNav"
    />

    <Loader
      v-if="$apollo.loading && !errors.length"
      label="Loading Campaign..."
      class="loader v-center"
    />

    <div v-if="!$apollo.loading && !errors.length" class="editor">
      <div
        class="w-full h-full md:w-gr-small sidebar-wrap"
        :class="{ 'sidebar-active': mobileSidebar }"
      >
        <Sidebar
          :campaign="campaign"
          :mobile-sidebar="mobileSidebar"
          :sending-test="sendingTest"
          @test-send="onTestSend"
        />
      </div>

      <!-- Preview Holder -->
      <div class="w-full h-full md:w-gr-large xl:flex-grow preview-holder">
        <Preview
          v-if="!$apollo.loading && campaign && !mobileSidebar"
          :mobile-sidebar="mobileSidebar"
          :query-input="previewQueryInput"
        />
      </div>
    </div>

    <WelcomeModal />

    <UpgradeFailedModal v-if="upgradeFailed" />

    <UpgradeSuccessfulModal v-if="upgradeSuccessful" />
  </div>
</template>

<script>
import Header from "@/components/Editor/Header.vue";
import Preview from "@/components/Editor/Preview.vue";
import Sidebar from "@/components/Editor/Sidebar/Sidebar.vue";
import Loader from "@/components/UI/Loader";
import { Campaign as CampaignQuery } from "@/graphql/queries";
import { TestSendCampaign } from "@/graphql/mutations";
import UpgradeFailedModal from "@/components/Account/UpgradeFailedModal";
import UpgradeSuccessfulModal from "@/components/Account/UpgradeSuccessfulModal";
import WelcomeModal from "@/components/Campaigns/WelcomeModal";
import EventBus from "@/lib/event-bus.js";

import { getAccountId, isAuthError } from "@/lib/auth";

export default {
  apollo: {
    campaign: {
      query: () => CampaignQuery,
      update: (data) => data.campaign,
      result({ data, loading }) {
        if (!loading && this.$has(data, "campaign") && data.campaign) {
          this.checkCampaignStatus();

          // Track the event in GTM...
          const { campaign } = data;

          this.$gtm.trackEvent({
            event: "promo-load-campaign",
            campaignId: campaign.campaignId,
            accountId: campaign.accountId,
          });
        }
      },
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          this.errors = graphQLErrors.map(({ message }) => message);

          this.$modal.show("error", {
            errors: this.errors,
            showCloseButton: false,
            errorAction: () => {
              this.$modal.hide("error");
              this.$router.push({
                name: "Campaigns",
              });
            },
            errorActionLabel: "Go to Campaigns",
          });
        }
      },
      variables: function() {
        return {
          input: { campaignId: this.$route.params.id },
        };
      },
    },
  },
  name: "Editor",
  components: {
    Header,
    Loader,
    Preview,
    Sidebar,
    UpgradeFailedModal,
    UpgradeSuccessfulModal,
    WelcomeModal,
  },
  data() {
    return {
      campaign: null,
      errors: [],
      sendingTest: false,
      mobileSidebar: false,
      mobileSend: false,
      upgradeFailed: false,
      upgradeSuccessful: false,
    };
  },
  computed: {
    accountId() {
      return this.$has(this.campaign, "accountId")
        ? this.campaign.accountId
        : "";
    },
    previewQueryInput() {
      return {
        campaignId: this.campaign?.campaignId,
        includeData: true,
      };
    },
  },
  metaInfo: {
    title: "Campaign",
  },
  mounted() {
    this.upgradeCheck();

    this.showWelcomeMessage();

    this.storeCampaignId();
  },
  methods: {
    checkCampaignStatus() {
      if (this.campaign?.status === "SENT") {
        this.$router.push({
          name: "Campaigns",
          query: {
            alertTitle: "Campaign already sent",
            alertMessage:
              "Sorry it looks like you're trying to access a campaign that was already sent. Please create a new one to continue.",
          },
        });
      }
    },
    handleUpgradeFailure() {
      this.upgradeFailed = true;

      this.$gtm.trackEvent({
        event: "promo-upgrade-failed",
        accountId: this.accountId,
        campaignId: this.$route.params.id,
      });

      // Show the modal once the DOM has updated...
      this.$nextTick(() => this.$modal.show("upgrade-failed"));
    },
    handleUpgradeSuccess() {
      this.upgradeSuccessful = true;

      this.$gtm.trackEvent({
        event: "promo-upgrade-complete",
        accountId: this.accountId,
        campaignId: this.$route.params.id,
      });

      // Show the modal once the DOM has updated...
      this.$nextTick(() => this.$modal.show("upgrade-successful"));
    },
    onMobileNav(nav) {
      this[nav] = !this[nav];
    },
    onTestSend: async function() {
      this.sendingTest = true;

      // Send mutation with the campaign Id
      try {
        await this.$apollo.mutate({
          mutation: TestSendCampaign,
          variables: {
            input: {
              campaignId: this.$route.params.id,
            },
          },
          update: (store, { data: { testSendCampaign } }) => {
            this.$notify({
              group: "general",
              type: "success",
              title: "Test successfully sent to...",
              text: testSendCampaign.email,
            });
          },
        });

        this.sendingTest = false;
      } catch (error) {
        this.sendingTest = false;

        this.$modal.show("dialog", {
          title: "Oh no 😯",
          text: `Sorry we couldn't send the test campaign. Please try again. If the problem persists please contact our <a href="mailto:support@promo.ai?subject=Promo.ai Error: Sending Test Campaign&body=%0D%0A%0D%0AError: ${error}">support team</a>.`,
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
    upgradeCheck() {
      const { upgradeSuccessful } = this.$route?.query;
      const { id } = this.$route?.params;
      // Check we have a campaign Id
      // And if an upgrade was successful
      if (upgradeSuccessful && id) {
        return upgradeSuccessful === "true"
          ? this.handleUpgradeSuccess()
          : this.handleUpgradeFailure();
      }
    },
    onUpgradeClick() {
      this.$gtm.trackEvent({
        event: "promo-upgrade-banner",
        accountId: getAccountId(),
      });

      this.$modal.show("package-selector");
    },
    showWelcomeMessage() {
      if (!window.localStorage.getItem("pai-hideWelcomeModal"))
        this.$modal.show("campaign-welcome");
    },
    storeCampaignId() {
      // Store the current campaignId in local storage
      // This enables us to load the most recent campaign
      // if a campaign isn't passed through in the url
      if (this.$has(this.$route, "params.id"))
        window.localStorage.setItem(
          "pai-currentCampaignId",
          this.$route.params.id
        );
    },
  },
};
</script>

<style scoped>
.editor {
  @apply flex flex-wrap h-full relative;
  padding-top: 93px;
  z-index: 1;

  @screen sm {
    padding-top: 80px;
  }

  @screen md {
    padding-top: 92px;
  }
}

.sidebar-wrap {
  @apply absolute;
  left: -100%;
  transition: left 300ms ease-in;
  z-index: 1;

  &.sidebar-active {
    left: 0;
  }

  @screen md {
    @apply relative;
    left: 0;
  }

  @screen xl {
    @apply max-w-sm;
  }
}

.preview-holder {
  @apply bg-brand-light border-l border-grey-light h-screen max-h-full max-w-full overflow-auto relative;
}

.upgrade-icon {
  @apply relative text-info-dark;
  top: 1px;
}

.upgrade-banner {
  @apply bg-info-light text-info-dark shadow rounded-lg relative m-2 mb-0 border py-2;
  border-color: #d1e1e6;
  z-index: 1;
}

.upgrade-button {
  @apply p-0 font-lato font-black underline text-info-dark text-sm;

  &:hover {
    @apply no-underline;
  }
}
</style>
